import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAtom, useSetAtom } from "jotai";
import { authAtom } from "../../states/auth";
import EmojiPicker from "emoji-picker-react";
import Image from "react-bootstrap/Image";
import { Button } from "react-bootstrap";
import chatBackground from "./chat-bg.jpg";
import {
  CheckIcon,
  DoubleCheckIcon,
  VideoCameraIcon,
  CameraIcon,
  MiceIcon,
  PlusIcon,
  SendIcon,
  XIcon,
} from "../../components/SVGIcon";
import { getChatMessages, messageRead, sendMessage } from "../../services/chatServices";
import { useAlert } from "../../contexts/AlertContext";
import { format, isToday, isYesterday } from "date-fns";
import { vonageAccessToken } from "../../services/vonageServices";
import ImagePreviewLightbox from "../../components/ImagePreviewLightbox";
import VideoPopupLightbox from "../../components/VideoPopupLightbox";
import eventBus from "../../utils/eventBus";
import CameraModal from "./CameraModal";
import ImagePreviewLightboxslide from "../../components/ImagePreviewLightboxslide";
import { chatLastMessageAtom, chatUnseenCountsAtom } from "../../states/chatAtoms";


const getLastMsgText = (msg) => {
  switch (msg.message_type) {
    case 3: return "{{IMAGE}}";
    case 4: return "{{VIDEO}}";
    case 5: return "{{MEDIA}}";
    case 6: return "{{MEDIA}}";
    case 2: return "📞 Video Call";
    default: return msg.message || "";
  }
};

const disallowedExtensions = [
  ".exe",
  ".bat",
  ".cmd",
  ".sh",
  ".js",
  ".msi",
  ".vbs",
  ".scr",
  ".pif",
  ".jar",
  ".com",
];

const getMessageTypeFromFile = (file) => {
  console.log("file", file);
  if (!file) return 1; // Default to text

  const imageTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
  const videoTypes = ["video/mp4", "video/webm"];
  const audioTypes = ["audio/mpeg", "audio/wav", "audio/webm", "audio/ogg", "audio/mp4"];
  const docTypes = ["audio/mpeg", "audio/wav"];

  if (imageTypes.includes(file.type)) return 3; // Image
  if (videoTypes.includes(file.type)) return 4; // Video
  if (audioTypes.includes(file.type)) return 5; // Audio

  return 6; // Generic file
};

const ChatMessages = ({
  conversationId,
  reorderConversations,
  conversationData,
  currentUserId,
  meetingId,
  setMeetingId,
}) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [callData, setCallData] = useState(null);
  const [chatPagination, setChatPagination] = useState({
    limit: 10,
    nextToken: null,
  });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const inputWrapperRef = useRef(null);
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const textareaRef = useRef();
  const chatContainerRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [auth] = useAtom(authAtom);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef(null);
  const [showCameraModal, setShowCameraModal] = useState(false);

  // Voice Recording (Web Only)
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const longPressTimer = useRef(null);
  const streamRef = useRef(null); // New: Store stream for cleanup
  const isCanceling = useRef(false); // New: Flag for cancel
  const LONG_PRESS_DELAY = 300;

  const setLastMessage = useSetAtom(chatLastMessageAtom);
  // Cleanup timers/listeners
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop());
    };
  }, []);

  const sendMessageNow = async ({ file = null, text = "" }) => {
    if (isSending) return;
    setIsSending(true);

    try {
      const type = file ? getMessageTypeFromFile(file) : 1;
      const formData = new FormData();
      formData.set("conversationID", conversationId);
      formData.set("type", type);
      formData.set("message", text.trim());
      if (file) formData.set("files", file);

      const messageData = await sendMessage(formData);

      const newMsg = {
        message_type: type,
        sender_id: currentUserId,
        message: file ? messageData?.data?.message : text.trim(),
        conversation_id: conversationId,
        metadata: messageData?.data?.metadata,
        created_date: new Date().toISOString(),
      };

      setChatMessages((prev) => [...prev, newMsg]);
      reorderConversations(conversationId);
      scrollToBottom();
      let payload = {
        messageID: messageData?.data?.id
      }
      await messageRead(conversationId, payload)
      setLastMessage((prev) => ({
        ...prev,
        [conversationId]: {
          text: getLastMsgText(newMsg),
          type: newMsg.message_type,
          createdAt: newMsg.createdAt || newMsg.created_at || new Date(),
        },
      }));
    } catch (err) {
      console.error("Send failed:", err);
      showAlert({
        title: "Error",
        message: "Failed to send message",
        variant: "danger",
      });
    } finally {
      setIsSending(false);
    }
  };

  const startVoiceRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const recorder = new MediaRecorder(stream);
      audioChunksRef.current = [];

      recorder.ondataavailable = (e) => {
        audioChunksRef.current.push(e.data);
      };

      recorder.onstop = () => {
        if (!isCanceling.current) {
          const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
          const audioFile = new File([audioBlob], `voice-${Date.now()}.webm`, {
            type: "audio/webm",
          });
          sendMessageNow({ file: audioFile });
        }

        // Cleanup
        audioChunksRef.current = [];
        isCanceling.current = false;
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

      };

      recorder.start();
      mediaRecorderRef.current = recorder;
      setIsRecording(true);
      setRecordingTime(0);

      timerRef.current = setInterval(() => {
        setRecordingTime(t => t + 1);
      }, 1000);
    } catch (err) {
      showAlert({
        title: "Microphone Error",
        message: "Please allow microphone access",
        variant: "danger",
      });
      setIsRecording(false);
    }
  };

  const stopVoiceRecording = (cancel = false) => {
    if (!isRecording) return;

    clearInterval(timerRef.current);
    setIsRecording(false);
    setRecordingTime(0);
    isCanceling.current = cancel;

    if (mediaRecorderRef.current?.state === "recording") {
      mediaRecorderRef.current.stop();
    }
  };

  const handleMicMouseDown = (e) => {
    e.preventDefault();

    // If text or files exist → send them (don't start recording)
    if (message.trim() || selectedFiles.length > 0) {
      handleKeyDown({ preventDefault: () => { }, _reactName: "onClick" });
      return;
    }

    longPressTimer.current = setTimeout(() => {
      startVoiceRecording();
    }, LONG_PRESS_DELAY);
  };

  const handleMicMouseUp = (e) => {
    e.preventDefault();
    clearTimeout(longPressTimer.current);

    if (isRecording) {
      const isCancel = e.type === "mouseleave" || e.type === "mouseup" && e.button !== 0;
      stopVoiceRecording(isCancel);
    }
  };
  useEffect(() => {
    function handleClickOutside(event) {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowEmojiPicker(false);
      }
    }

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showEmojiPicker]);

  useEffect(() => {
    // alert(meetingId);
    if (meetingId && meetingId != null && meetingId?.length > 25) {
      // alert(meetingId);
      const formData = new FormData();
      formData.set("conversationID", conversationId);

      let type = 2;

      formData.set("type", type);
      formData.set("message", meetingId);

      try {
        const newMsg = {
          message_type: type,
          sender_id: currentUserId,
          message: selectedFile
            ? URL.createObjectURL(selectedFile)
            : meetingId.trim(),
          conversation_id: conversationId,
          created_date: new Date().toISOString(),
        };

        setChatMessages((prev) => [...prev, newMsg]);
        setMessage("");
        setSelectedFile(null);
        scrollToBottom();
      } catch (error) {
        showAlert({
          title: "Chat Screen Notification",
          message: error?.response?.data?.message ?? "Something went wrong",
          variant: "danger",
        });
      }

      setMeetingId(null);
    }
  }, [meetingId]);
  // Adjust textarea height
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    if (textareaRef.current) {
      const el = textareaRef.current;

      // Reset to shrink if text is deleted
      el.style.height = "auto";

      const lineHeight = parseInt(window.getComputedStyle(el).lineHeight, 10);
      const scrollHeight = el.scrollHeight;

      // Max lines allowed before scroll
      const maxLines = 6;
      const maxHeight = lineHeight * maxLines;

      if (scrollHeight <= maxHeight) {
        // Grow naturally with content
        el.style.height = scrollHeight + "px";
        el.style.overflowY = "hidden";
      } else {
        // Cap at max height, enable scroll
        el.style.height = maxHeight + "px";
        el.style.overflowY = "auto";
      }
    }
  }, [message]);

  const scrollToBottom = () => {
    const container = chatContainerRef.current;
    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  };

  const onFileLinkClick = (event, url) => {
    console.log(event, url);
    event.preventDefault();
  };

  const onMeetingLinkClick = (event, callId) => {
    event.preventDefault();
    try {
      console.log("asdasd", auth.user.firstName);
      const callRequest = {
        roomName: callId,
        identifierName: auth?.user?.firstName + auth?.user?.lastName,
      };

      const data = vonageAccessToken(callRequest).then((resp) => {
        console.log("response", resp);
        localStorage.setItem("vonage_session_id", resp.sessionId);
        localStorage.setItem("vonage_token", resp.token);
        localStorage.setItem("vonage_cid", conversationData?.id);
        localStorage.setItem("vonage_meetingId", callId);
        localStorage.setItem("token", auth?.token);
        navigate("/connect");
        // const newMsg = {
        //   message_type: type,
        //   sender_id: currentUserId,
        //   message: selectedFile
        //     ? URL.createObjectURL(selectedFile)
        //     : message.trim(),
        //   conversation_id: conversationId,
        //   created_date: new Date().toISOString()
        // };

        // setChatMessages(prev => [...prev, newMsg]);
        // setMessage('');
        // setSelectedFile(null);
        // scrollToBottom();
      });
    } catch (error) {
      showAlert({
        title: "Chat Screen Notification",
        message: error?.response?.data?.message ?? "Something went wrong",
        variant: "danger",
      });
    }
  };

  const handleKeyDown1 = async (e) => {
    if ((e.key === "Enter" && !e.shiftKey) || e._reactName === "onClick") {
      e.preventDefault();

      if (isSending) return;
      if (!message.trim() && !selectedFile) return;

      setIsSending(true); // lock
      const formData = new FormData();
      formData.set("conversationID", conversationId);

      let type = 1;
      if (selectedFile) {
        type = getMessageTypeFromFile(selectedFile);
        formData.set("files", selectedFile);
      } else {
        formData.set("files", null);
      }

      formData.set("type", type);
      formData.set("message", message.trim());

      try {
        const messageData = await sendMessage(formData);
        console.log("messageData", messageData);
        const newMsg = {
          message_type: type,
          sender_id: currentUserId,
          message: selectedFile ? messageData?.data?.message : message.trim(),
          conversation_id: conversationId,
          created_date: new Date().toISOString(),
          metadata: messageData?.data?.metadata
        };
        console.log("newMessage", newMsg)
        setChatMessages((prev) => [...prev, newMsg]);
        setMessage("");
        reorderConversations(conversationId);
        setSelectedFile(null);
        scrollToBottom();
        let payload = {
          messageID: messageData?.data?.id
        }
        await messageRead(conversationId, payload)
        setLastMessage((prev) => ({
          ...prev,
          [conversationId]: {
            text: getLastMsgText(newMsg),
            type: newMsg.message_type,
             createdAt: newMsg.createdAt || newMsg.created_at || new Date(),
          },
        }));
      } catch (error) {
        alert();
        // showAlert({
        //   title: "Chat Screen Notification",
        //   message: error?.response?.data?.message ?? "Something went wrong",
        //   variant: "danger",
        // });
      } finally {
        setIsSending(false); // unlock
      }
    }
  };

  const handleKeyDown = async (e) => {
    if ((e.key === "Enter" && !e.shiftKey) || e._reactName === "onClick") {
      e.preventDefault();
      if (isSending) return;
      if (!message.trim() && selectedFiles.length === 0) return;

      setIsSending(true);
      setShowEmojiPicker(false);

      try {
        let sentMessages = [];

        const isBulkImages = selectedFiles.length > 1 && selectedFiles.every(f => f.type.startsWith('image/'));
        const bulkImageUniqueID = isBulkImages ? `bulk_${Date.now()}_${Math.random().toString(36).substr(2, 9)}` : null;

        if (selectedFiles.length > 0) {
          for (const file of selectedFiles) {
            const type = getMessageTypeFromFile(file);
            const isImage = type === 3;

            const formData = new FormData();
            formData.set("conversationID", conversationId);
            formData.set("files", file);
            formData.set("type", type);
            formData.set("message", message.trim());
            formData.set("additionalText", message.trim());

            // Optional: Send bulkImageUniqueID to backend if it supports it
            if (isBulkImages && isImage) {
              formData.set("bulkImageUniqueID", bulkImageUniqueID);
            }

            const messageData = await sendMessage(formData);
            let payload = {
              messageID: messageData?.data?.id
            }
            const newMsg = {
              message_type: type,
              sender_id: currentUserId,
              message: messageData?.data?.message,
              conversation_id: conversationId,
              metadata: messageData?.data?.metadata,
              created_date: new Date().toISOString(),
              // Add group ID for frontend grouping
              groupId: isBulkImages && isImage ? bulkImageUniqueID : null,
              additionalText: message.trim(), // caption
            };

            setChatMessages((prev) => [...prev, newMsg]);
            await messageRead(conversationId, payload);
            setLastMessage((prev) => ({
              ...prev,
              [conversationId]: {
                text: getLastMsgText(newMsg),
                type: newMsg.message_type,
                createdAt: newMsg.createdAt || newMsg.created_at || new Date(),
              },
            }));
          }

          setSelectedFiles([]);
          setMessage("");
          reorderConversations(conversationId);
          scrollToBottom();

        } else if (message.trim()) {
          // Send text-only message
          const formData = new FormData();
          formData.set("conversationID", conversationId);
          formData.set("files", null);
          formData.set("type", 1);
          formData.set("message", message.trim());

          const messageData = await sendMessage(formData);
          const newMsg = {
            message_type: 1,
            sender_id: currentUserId,
            message: message.trim(),
            conversation_id: conversationId,
            created_date: new Date().toISOString(),
          };

          setChatMessages((prev) => [...prev, newMsg]);
          setMessage("");
          reorderConversations(conversationId);
          scrollToBottom();
          let payload = {
            messageID: messageData?.data?.id
          }
          await messageRead(conversationId, payload)
          setLastMessage((prev) => ({
            ...prev,
            [conversationId]: {
              text: getLastMsgText(newMsg),
              type: newMsg.message_type,
             createdAt: newMsg.createdAt || newMsg.created_at || new Date(),
            },
          }));
        }
      } catch (error) {
        showAlert({
          title: "Chat Screen Notification",
          message: error?.response?.data?.message ?? "Something went wrong",
          variant: "danger",
        });
      } finally {
        setIsSending(false);
        if (textareaRef.current) textareaRef.current.focus();
      }
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    // const file = e.target.files[0];

    const validFiles = files.filter((file) => {
      const fileName = file.name.toLowerCase();
      const isDisallowed = disallowedExtensions.some((ext) =>
        fileName.endsWith(ext)
      );
      if (isDisallowed) {
        showAlert({
          title: "File Upload Error",
          message: `Files with these extensions are not allowed: ${disallowedExtensions.join(
            ", "
          )}`,
          variant: "danger",
        });
        return false;
      }
      return true;
    });

    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      if (textareaRef.current) textareaRef.current.focus();
    }

    // if (file) {
    //   const fileName = file.name.toLowerCase();

    //   const isDisallowed = disallowedExtensions.some(ext => fileName.endsWith(ext));

    //   if (isDisallowed) {
    //     showAlert({
    //       title: "File Upload Error",
    //       message: `Files with these extensions are not allowed: ${disallowedExtensions.join(', ')}`,
    //       variant: "danger",
    //     });
    //     e.target.value = ""; // reset input so same file can be chosen again
    //     return;
    //   }

    //   setSelectedFile(file);

    //   if (textareaRef.current) {
    //     textareaRef.current.focus();
    //   }
    // }

    // Reset input so selecting the same file again will fire onChange
    e.target.value = "";
  };

  const getFileIcon = (file) => {
    if (!file) return "/images/file-type-icon-doc.svg";

    const type = file.type || "";
    const ext = (file.name || "").split(".").pop().toLowerCase();

    // Show actual preview for images
    if (type.startsWith("image/")) {
      return URL.createObjectURL(file);
    }

    // Show actual preview for video (thumbnail generation would need <video>, but at least preview)
    if (type.startsWith("video/") || type.startsWith("audio/")) {
      return URL.createObjectURL(file);
    }

    // Otherwise, return a static icon based on extension
    if (ext === "pdf") return "/images/file-type-icon-pdf.svg";
    if (["doc", "docx"].includes(ext)) return "/images/file-type-icon-doc.svg";
    if (["xls", "xlsx"].includes(ext)) return "/images/file-type-icon-doc.svg";
    if (["ppt", "pptx"].includes(ext)) return "/images/file-type-icon-doc.svg";
    // if (["zip", "rar"].includes(ext)) return "/images/file-type-icon-zip.svg";
    if (ext === "txt") return "/images/file-type-icon-doc.svg";

    // Default fallback
    return "/images/file-type-icon-doc.svg";
  };

  const getMessages = async (isLoadMore = false) => {
    try {
      if (isLoadMore) setIsLoadingMore(true);

      const data = await getChatMessages(conversationId, chatPagination);
      const newMessages = data?.data?.items || [];

      if (newMessages.length > 0) {
        newMessages.sort(
          (a, b) => new Date(a.created_date) - new Date(b.created_date)
        );
      }

      setChatMessages((prev) => {
        const merged = isLoadMore ? [...newMessages, ...prev] : newMessages;
        const seen = new Set();
        return merged.filter((msg) => {
          const key = msg.message_id || `${msg.created_date}_${msg.sender_id}`;
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      });

      if (data?.data?.nextToken) {
        setChatPagination((prev) => ({
          ...prev,
          nextToken: data.data.nextToken,
        }));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      showAlert({
        title: "Chat Screen Notification",
        message: error?.response?.data?.message ?? "Something went wrong",
        variant: "danger",
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  const [, setUnseenCounts] = useAtom(chatUnseenCountsAtom);

  useEffect(() => {
    if (!conversationId || chatMessages.length === 0) return;

    const lastMessage = chatMessages[chatMessages.length - 1];
    const lastMessageId = lastMessage.message_id || lastMessage.id;

    if (!lastMessageId) return;

    const timer = setTimeout(async () => {
      try {
        await messageRead(conversationId, { messageID: lastMessageId });
        setUnseenCounts((prev) => {
          const updated = { ...prev };
          delete updated[conversationId];
          return updated;
        });
      } catch (error) {
        console.error("Failed to mark as read:", error);
      }
    }, 1000);

    return () => clearTimeout(timer);
  }, [conversationId, chatMessages.length]);

  const handleScroll = () => {
    const container = chatContainerRef.current;
    if (!container || !hasMore || isLoadingMore) return;

    if (container.scrollTop < 50) {
      const prevHeight = container.scrollHeight;
      getMessages(true).then(() => {
        requestAnimationFrame(() => {
          const newHeight = container.scrollHeight;
          container.scrollTop = newHeight - prevHeight;
        });
      });
    }
  };

  const groupMessagesByDate = (messages) => {
    const grouped = {};
    messages.forEach((msg) => {
      const dateObj = new Date(msg.created_date);
      let label = format(dateObj, "dd MMM yyyy");
      if (isToday(dateObj)) label = "Today";
      else if (isYesterday(dateObj)) label = "Yesterday";

      if (!grouped[label]) grouped[label] = [];
      grouped[label].push(msg);
    });
    return grouped;
  };

  const extractFileName = (url) => {
    const lastPart = url.substring(url.lastIndexOf("/") + 1);
    const fileName = lastPart.substring(lastPart.indexOf("_") + 1);
    return fileName || "";
  };

  useEffect(() => {
    const handleEnterForFile = (e) => {
      // Only trigger if Enter pressed, no Shift, and file is selected
      if (e.key === "Enter" && !e.shiftKey && selectedFile) {
        e.preventDefault();
        handleKeyDown(e);
      }
    };

    const wrapper = inputWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener("keydown", handleEnterForFile);
    }

    return () => {
      if (wrapper) {
        wrapper.removeEventListener("keydown", handleEnterForFile);
      }
    };
  }, [selectedFile, message]);

  useEffect(() => {
    if (conversationId != null) {
      console.log("conversationData", conversationData);
      setCallData(
        (conversationData?.calls && conversationData?.calls[0]) || null
      );
      setChatMessages([]);
      setChatPagination({ limit: 20, nextToken: null });
      setHasMore(true);
      getMessages(false);
    }
    const handleNewMessage = (data) => {
      setLastMessage((prev) => ({
        ...prev,
        [data.conversation_id]: {
          text: getLastMsgText(data),
          // type: data.message_type,
          createdAt: data.createdAt || data.created_at || new Date(),
        },
      }));
      if (data.conversation_id === conversationId) {
        setChatMessages((prev) => [...prev, data]);
        scrollToBottom();
      } else {
        eventBus.emit("UNSEEN_MESSAGE", data);
      }
    };

    eventBus.on("NEW_MESSAGE", handleNewMessage);

    return () => {
      eventBus.off("NEW_MESSAGE", handleNewMessage);
    };
  }, [conversationId]);

  useLayoutEffect(() => {
    if (!isLoadingMore && chatMessages.length > 0) {
      scrollToBottom();
    }
  }, [chatMessages]);

  const emojiOnlyRegex =
    /^(?:\p{Emoji_Presentation}|\p{Emoji}\uFE0F|\p{Emoji_Modifier_Base}\p{Emoji_Modifier}?)+$/u;

  // Simple regex (covers most emojis, can be expanded if needed)
  function getEmojiClass(message = "") {
    if (!message) return "emoji-text";

    // Capture all emojis
    const emojiRegex = /\p{Extended_Pictographic}/gu;
    const matches = message.match(emojiRegex) || [];

    // Clean message: remove variation selectors + trim spaces
    const cleaned = message.replace(/\uFE0F/g, "").trim();

    // If message is only emojis
    if (matches.length > 0 && matches.join("") === cleaned) {
      if (matches.length === 1) return "emoji-1";
      if (matches.length === 2) return "emoji-2";
      if (matches.length === 3) return "emoji-3";
      return "emoji-text"; // 4 or more emojis
    }

    return "emoji-text"; // text or mixed
  }

  const handleEmojiClick = (emojiData) => {
    setMessage((prev) => prev + emojiData.emoji);
  };

  const handleCapturedPhoto = (file) => {
    setSelectedFiles([file]);   // now the captured photo goes to preview
    if (textareaRef.current) textareaRef.current.focus();
  };

  return (
    <>
      {chatMessages.length === 0 && conversationId == null ? (
        <div className="bg-gray-50 d-flex flex-column align-items-center justify-content-center h-100 text-center">
          <img src="images/chat-messaging-icon.svg" alt="Chat Messaging Icon" />
          <h2 className="fs-36 fw-semibold text-gray-700 mt-3 mb-1">
            Welcome to AssistChat
          </h2>
          <p className="fs-20 fw-medium text-gray-500 mb-0">
            It all starts with a hello—type your message and we're good to go!
          </p>
        </div>
      ) : (
        <>
          <div
            className="chat-message-body"
            ref={chatContainerRef}
            onScroll={handleScroll}
            style={{
              backgroundImage: `url(${chatBackground})`,
              overflowY: "auto",
              height: "100%",
            }}
          >
            {conversationData?.conversationType &&
              !conversationData?.conversationType?.includes("chat") && (
                <div className="chat-message-note bg-green-200 text-green-600 p-2 text-center fs-16 lh-lg fw-semibold">
                  Note: This chat will remain active until the session ends
                </div>
              )}
            {conversationData?.conversationStatus === "Pending" && (
              <div className="badge-warning chat-message-note  p-2 text-center fs-16 lh-lg fw-semibold">
                <span className="badge-warning">Pending: An expert will respond shortly</span>
              </div>
            )}
            {(chatMessages.length > 0 && (
              <>
                {isLoadingMore && (
                  <div className="text-center my-2">
                    <span
                      className="spinner-border text-primary"
                      role="status"
                    ></span>
                  </div>
                )}
                {Object.entries(groupMessagesByDate(chatMessages)).map(
                  ([dateLabel, messages], index) => (
                    <React.Fragment key={dateLabel + index}>
                      <div className="text-center my-2">
                        <span className="px-3 py-2 bg-gray-50 text-gray-500 rounded-2 d-inline-block">
                          {dateLabel}
                        </span>
                      </div>
                      {messages.map((msg, i) => {
                        const isSelf = msg.sender_id === currentUserId;
                        const time = new Date(
                          msg.created_date
                        ).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true
                        });

                        if (msg.message_type === 2) {
                          return (
                            <div
                              key={msg.created_date + i}
                              className={`chat-message ${isSelf
                                ? "right bg-blue-200 ms-auto"
                                : "left me-auto bg-white"
                                } `}
                            >
                              <Link
                                onClick={(event) =>
                                  onMeetingLinkClick(event, msg?.message)
                                }
                                className="cursor-pointer text-decoration-none d-flex align-items-center w-100 gap-3 text-gray-700">
                                <div
                                  className={`video-chat-icon ${isSelf
                                    ? "bg-white text-blue-600"
                                    : "bg-blue-200"
                                    }`}
                                >
                                  <VideoCameraIcon size={32} />
                                </div>
                                <div>
                                  <p
                                    className="fs-16 fw-bold mb-1 text-gray-700">
                                    Video Call
                                  </p>
                                  <span
                                    className="fs-14 fw-medium text-gray-500">
                                    Click here to join the video call
                                  </span>
                                </div>
                              </Link>
                              {msg?.metadata?.t && <span className="mt-2">{msg?.metadata?.t}</span>}
                              <div
                                className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end w-100 gap-1 mt-1 text-gray-600">
                                {time} <CheckIcon />
                              </div>
                            </div>
                          );
                        }
                        if (msg.message_type === 3) {
                          const groupId = msg.groupId || msg.metadata?.b;
                          if (!groupId) {
                            return (
                              <div
                                key={msg.created_date}
                                className={`chat-message ${isSelf ? "right ms-auto bg-blue-200" : "left me-auto bg-white"} p-2 rounded-3`}
                              // style={{ maxWidth: "85%" }}
                              >
                                <div className="image-album image-album-1 rounded-3 overflow-hidden">
                                  <div className="album-item">
                                    <ImagePreviewLightboxslide images={[msg.message]} initialSlide={0}>
                                      <Image src={msg.message} fluid className="w-100 h-100 object-fit-cover" />
                                    </ImagePreviewLightboxslide>
                                  </div>
                                </div>
                                {(msg.additionalText || msg?.metadata?.t) && (
                                  <p className="mt-3 mb-0 px-3 text-gray-700 fs-14">
                                    {msg.additionalText || msg?.metadata?.t}
                                  </p>
                                )}
                                <div className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end gap-1 mt-2 px-3 pb-1 text-gray-600">
                                  {time} <DoubleCheckIcon />
                                </div>
                              </div>
                            );
                          }
                          const firstIndexInGroup = chatMessages.findIndex(m =>
                            m.message_type === 3 && (m.groupId || m.metadata?.b) === groupId
                          );


                          if (chatMessages.indexOf(msg) !== firstIndexInGroup) {
                            return null;
                          }

                          const groupMessages = chatMessages.filter(m =>
                            m.message_type === 3 && (m.groupId || m.metadata?.b) === groupId
                          );

                          const imagesCount = groupMessages.length;
                          const showOverlay = imagesCount > 4;
                          const displayedImages = showOverlay ? groupMessages.slice(0, 4) : groupMessages;
                          const caption =
                            groupMessages[0]?.additionalText ||
                            groupMessages[0]?.metadata?.t ||
                            "";
                          const lastMsg = groupMessages[imagesCount - 1];
                          const groupTime = new Date(lastMsg.created_date).toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          });

                          const groupKey = groupId + caption;

                          return (
                            <div
                              key={groupKey}
                              className={`chat-message ${isSelf ? "right ms-auto bg-blue-200" : "left me-auto bg-white"} p-2 rounded-3`}
                              style={{ maxWidth: "85%" }}
                            >
                              <div className={`image-album image-album-${Math.min(imagesCount, 4)} rounded-3 overflow-hidden`}>
                                {displayedImages.map((imgMsg, displayIndex) => {
                                  const slideIndex = groupMessages.findIndex(m => m.message === imgMsg.message);

                                  return (
                                    <div key={imgMsg.created_date} className="album-item position-relative">
                                      <ImagePreviewLightboxslide
                                        images={groupMessages.map(m => m.message)}
                                        initialSlide={slideIndex >= 0 ? slideIndex : 0}
                                      >
                                        <Image
                                          src={imgMsg.message}
                                          alt="Sent photo"
                                          fluid
                                          className="w-100 h-100 object-fit-cover"
                                          loading="lazy"
                                          onError={(e) => { e.target.src = "/images/image-broken.svg"; }}
                                        />
                                      </ImagePreviewLightboxslide>

                                      {showOverlay && displayIndex === 3 && (
                                        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-black bg-opacity-60 text-white fs-36 fw-bold">
                                          +{imagesCount - 4}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>

                              {caption && (
                                <p className="mt-3 mb-0 px-3 text-gray-700 fs-14">{caption}</p>
                              )}

                              <div className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end gap-1 mt-2 px-3 pb-1 text-gray-600">
                                {groupTime} <DoubleCheckIcon />
                              </div>
                            </div>
                          );
                        }

                        if (msg.message_type === 4) {
                          return (
                            <div
                              key={msg.created_date + i}
                              className={`chat-message ${isSelf
                                ? "right ms-auto bg-blue-200"
                                : "left"
                                } `}
                            >
                              <div className="message-attachment-box">
                                <Image
                                  className="position-absolute top-50 start-50 translate-middle pe-none z-1"
                                  src="images/play-rounded.svg"
                                  width={24}
                                  height={24}
                                  alt="Play Icon"
                                />
                                <VideoPopupLightbox
                                  className="ratio ratio-1x1"
                                  videoSlide={{
                                    type: "video",
                                    sources: [
                                      {
                                        src: msg?.message,
                                        type: "video/mp4",
                                      },
                                    ],
                                  }}
                                >
                                  <video className="rounded-1 object-fit-cover">
                                    <source src={msg?.message} />
                                  </video>
                                </VideoPopupLightbox>
                                <div
                                  className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end gap-1 mt-1 text-gray-600">
                                  {time} <DoubleCheckIcon />
                                </div>
                              </div>
                              {msg?.metadata?.t && <span className="mt-2">{msg?.metadata?.t}</span>}
                            </div>
                          );
                        }

                        if (msg.message_type === 5) {
                          return (
                            <div
                              key={msg.created_date + i}
                              className={`chat-message ${isSelf ? "right ms-auto px-0" : "left"
                                } `}
                            >
                              <audio controls>
                                <source src={msg?.message} type="audio/mp4" />
                                Your browser does not support the audio element.
                              </audio>
                              {msg?.metadata?.t && <span className="mt-2">{msg?.metadata?.t}</span>}
                            </div>
                          );
                        }

                        if (msg.message_type === 6) {
                          return (
                            <>
                              <div
                                key={msg.created_date + i}
                                className={`chat-message ${isSelf
                                  ? "right bg-blue-200 ms-auto"
                                  : "left me-auto bg-white"
                                  } `}
                              >
                                <Link
                                  to={msg?.message}
                                  reloadDocument
                                  target="_blank"
                                  // onClick={(event)=>onFileLinkClick(event, msg?.message)}
                                  className="cursor-pointer text-decoration-none d-flex align-items-center w-100 gap-3 text-gray-700">
                                  <div
                                    className={`video-chat-icon ${isSelf
                                      ? "bg-white text-blue-600"
                                      : "bg-blue-200 text-white"
                                      }`}
                                  >
                                    <Image
                                      src="images/file-type-icon-doc.svg"
                                      alt="Icon"
                                    />
                                  </div>
                                  <div>
                                    <p
                                      className="fs-16 fw-bold mb-1 text-gray-700">
                                      {extractFileName(msg?.message)}
                                    </p>
                                    <span
                                      className="fs-14 fw-medium text-gray-500">
                                      Click here to download file
                                    </span>
                                  </div>
                                </Link>

                                {msg?.metadata?.t && <span className="mt-2">{msg?.metadata?.t}</span>}
                                <div
                                  className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end w-100 gap-1 mt-1 text-gray-600">
                                  {time} <CheckIcon />
                                </div>
                              </div>
                            </>
                          );
                        }

                        if (msg.message_type === 7) {
                          return (
                            <>
                              <div
                                key={msg.created_date + i}
                                className={`chat-message ${isSelf
                                  ? "right bg-blue-200 ms-auto"
                                  : "left me-auto bg-white"
                                  } `}
                              >
                                <Link
                                  onClick={(event) =>
                                    onMeetingLinkClick(event, msg?.message)
                                  }
                                  className="cursor-pointer text-decoration-none d-flex align-items-center w-100 gap-3 text-gray-600">
                                  <div
                                    className={`video-chat-icon ${isSelf
                                      ? "bg-white text-blue-600"
                                      : "bg-blue-200 text-white"
                                      }`}
                                  >
                                    <VideoCameraIcon size={32} />
                                  </div>
                                  <div>
                                    <p
                                      className="fs-16 fw-bold mb-1 text-gray-600">
                                      Video Call
                                    </p>
                                    <span
                                      className="fs-14 fw-medium text-gray-500">
                                      Click here to join the video call
                                    </span>
                                  </div>
                                </Link>
                                <div
                                  className="fs-11 fw-normal opacity-75 d-flex align-items-center justify-content-end w-100 gap-1 mt-1 text-gray-600">
                                  {time} <CheckIcon />
                                </div>
                                {msg?.metadata?.t && <span className="mt-2"> {msg?.metadata?.t}</span>}
                              </div>
                            </>
                          );
                        }

                        return (
                          <div
                            key={msg.created_date + i}
                            className={`chat-message ${getEmojiClass(
                              msg.message
                            )} ${isSelf
                              ? "right bg-blue-200 ms-auto"
                              : "left me-auto bg-white"
                              } `}
                          >
                            <div
                              className="fw-medium word-break text-gray-700">
                              {emojiOnlyRegex.test(msg.message) ? (
                                // just show emoji, no splitting
                                <span>{msg.message}</span>
                              ) : (
                                // normal text messages → preserve line breaks
                                msg.message
                                  .split(/(\r\n|\n|\r)/g)
                                  .map((line, index) =>
                                    line.match(/(\r\n|\n|\r)/) ? (
                                      <br key={index} />
                                    ) : (
                                      <span key={index}>{line}</span>
                                    )
                                  )
                              )}
                            </div>
                            <div
                              className="chat-message-status fs-11 fw-normal d-flex align-items-center justify-content-end w-100 gap-1 mt-1 text-gray-600">
                              {time} <DoubleCheckIcon />
                            </div>
                          </div>
                        );
                      })}
                    </React.Fragment>
                  )
                )}
              </>
            )) || (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-center">
                  <img
                    src="images/chat-messaging-icon.svg"
                    alt="Chat Messaging Icon"
                  />
                  <h2 className="fs-36 fw-semibold text-gray-700 mt-3 mb-1">
                    Welcome to Chat
                  </h2>
                  <p className="fs-20 fw-medium text-gray-500 mb-0">
                    It all starts with a hello—type your message and we're good to
                    go!
                  </p>
                </div>
              )}
          </div>

          {/* {selectedFile && (
            <div className="chat-attachment-preview">
              <div className="text-end p-4">
                <Button variant="reset" className="text-gray-500" onClick={() => setSelectedFile(null)}>
                  <XIcon size={24} />
                </Button>
              </div>
              <div className="chat-attachment-preview-box">
                <picture className="bg-gray-50 rounded-2 ratio ratio-4x3 d-block">
                  <Image
                    className="w-100 h-100 object-fit-contain p-3"
                    src={getFileIcon(selectedFile)}
                    alt={selectedFile.name}
                  />
                </picture>
                <p className="fs-14 text-gray-600 text-center mt-2 mb-0">
                  {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
                </p>
              </div>
            </div>
          )} */}
          {selectedFiles.length > 0 && (
            <div className="chat-attachment-preview">
              <picture className="ratio ratio-21x9 d-block my-2">
                <Image
                  className="w-100 h-100 object-fit-contain p-1"
                  src={getFileIcon(selectedFiles[0])}
                  alt={selectedFiles[0].name}
                />
              </picture>
              <div className="d-flex flex-wrap justify-content-center gap-1 p-3">
                {selectedFiles.map((file, index) => (
                  <>
                    <div
                      key={index}
                      className="chat-attachment-preview-item text-center position-relative"
                    >
                      {/* Remove single file */}
                      <Button
                        variant="reset"
                        className="position-absolute top-0 end-0 me-2 text-gray-500"
                        style={{ zIndex: 9 }}
                        onClick={() =>
                          setSelectedFiles((prev) =>
                            prev.filter((_, i) => i !== index)
                          )
                        }
                      >
                        <XIcon size={12} />
                      </Button>

                      <picture className="bg-white rounded-2 ratio ratio-1x1 d-block">
                        <Image
                          className="w-100 h-100 object-fit-contain p-1"
                          src={getFileIcon(file)}
                          alt={file.name}
                        />
                      </picture>
                      <p
                        className="fs-14 text-gray-600 mt-2 mb-0 text-truncate"
                        title={file.name}
                        style={{ maxWidth: "150px" }}
                      >
                        {file.name} ({(file.size / 1024).toFixed(2)} KB)
                      </p>
                    </div>
                  </>
                ))}
              </div>
            </div>
          )}

          <div
            ref={inputWrapperRef}
            className="chat-input-box bg-white border-top border-grey-200 d-flex align-items-center justify-content-between gap-3"
          >
            <div className="chat-attachment z-3">
              <input
                disabled={conversationData?.conversationStatus === "Pending"}
                type="file"
                id="file-upload"
                multiple
                className="d-none"
                onChange={handleFileChange}
              />
              <label htmlFor="file-upload" className="chat-attachment-btn">
                <PlusIcon />
              </label>
            </div>
            <div ref={pickerRef} className="chat-input-wrapper position-relative w-100 z-3">
              <Button
                disabled={conversationData?.conversationStatus === "Pending"}
                variant="reset"
                className="emoji-button"
                onClick={() => setShowEmojiPicker((val) => !val)}
              >
                <img src="images/emoji-icon.svg" width={24} height={24} alt="Emoji Icon" />
              </Button>
              {showEmojiPicker && (
                <EmojiPicker
                  className="emojipicker"
                  onEmojiClick={handleEmojiClick}
                  theme="light"
                  emojiStyle="apple"
                  skinTonesDisabled={false}
                  searchDisabled={false}
                  previewConfig={{ showPreview: false }}
                  height={450}
                  width={400}
                />
              )}
              <textarea
                disabled={conversationData?.conversationStatus === "Pending"}
                ref={textareaRef}
                id="message"
                name="message"
                className="form-control"
                placeholder="Write your message here...."
                rows={1}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={handleKeyDown}
                aria-label="Message input field"
                style={{
                  resize: "none"
                }}
              />
              <Button
                onClick={(e) => handleKeyDown(e)}
                variant="reset"
                className="text-blue-600"
              >
                <SendIcon />
              </Button>
            </div>
            <Button
              disabled={conversationData?.conversationStatus === "Pending"}
              variant="reset"
              className="chat-action-btn text-gray-500"
              onClick={() => setShowCameraModal(true)}
            >
              <CameraIcon />
            </Button>
            <Button
              disabled={conversationData?.conversationStatus === "Pending"}
              variant="reset"
              className={`chat-action-btn mic-btn ${isRecording ? 'recording' : ''}`}
              onMouseDown={handleMicMouseDown}
              onMouseUp={handleMicMouseUp}
              onMouseLeave={handleMicMouseUp}
              style={{ userSelect: 'none' }}
            >
              <MiceIcon />
            </Button>
          </div>
        </>
      )}
      {showCameraModal && (
        <CameraModal
          onClose={() => setShowCameraModal(false)}
          onCapture={handleCapturedPhoto}
        />
      )}
      {isRecording && (
        <div
          style={{
            position: "fixed",
            bottom: 90,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(0,0,0,0.9)",
            color: "white",
            padding: "12px 28px",
            borderRadius: 50,
            fontWeight: "bold",
            fontSize: 15,
            zIndex: 9999,
            backdropFilter: "blur(12px)",
            boxShadow: "0 4px 20px rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div className="recording-pulse"></div>

          <span>Recording</span>

          {/* Beautiful time format: 00:05 */}
          <span style={{ fontVariantNumeric: "tabular-nums" }}>
            {String(Math.floor(recordingTime / 60)).padStart(2, "0")}:
            {String(recordingTime % 60).padStart(2, "0")}
          </span>

          <span style={{ opacity: 0.8, fontSize: 13 }}>
            {recordingTime < 10 ? "Release to send" : "Move away to cancel"}
          </span>
        </div>
      )}
    </>
  );
};

export default ChatMessages;
